import { useMemo } from "react";
import { Check, UsersRound, Shrink, KeyRound, BedDouble, Sunrise, Sunset, HeartHandshake, SlidersHorizontal } from "lucide-react";
import { useSilver } from "@/context/SilverContext";
import { AUDIENCE_PRESETS, isPresetActive, presetFilters, representAsPresets } from "@/data/presets";
import { countForFilters } from "@/lib/audienceEngine";
import { AnimatedNumber } from "@/components/silver/common/AnimatedNumber";
import { cn } from "@/lib/utils";

const ICONS = { all: UsersRound, downsizers: Shrink, capital: KeyRound, larger: BedDouble, pre: Sunrise, retired: Sunset, living: HeartHandshake };

// Compact so all eight options fit on one screen above the fixed build bar.
function PresetCard({ preset, active, count, hasAreas, onClick }) {
  const Icon = ICONS[preset.id];
  return (
    <button type="button" onClick={onClick} aria-pressed={active} data-active={active}
      className={cn("glass glass-hover trace group flex min-h-[176px] flex-col items-start p-4 text-left", active && "glass-active")}
      data-testid={`preset-card-${preset.id}`}>
      <div className="flex w-full items-center justify-between">
        <span className={cn("grid h-9 w-9 place-items-center rounded-full border transition-colors duration-500", active ? "border-[#96C11F]/60 bg-[#96C11F]/20 text-[#C6EE5A]" : "border-white/10 bg-white/5 text-white/60")}>
          <Icon className="h-4 w-4" />
        </span>
        <span className={cn("grid h-5 w-5 place-items-center rounded-full border transition-all duration-500", active ? "scale-100 border-[#96C11F] bg-[#96C11F] text-[#12290F]" : "scale-90 border-white/15 text-transparent")} aria-hidden="true">
          <Check className="h-3 w-3" strokeWidth={3} />
        </span>
      </div>
      <h4 className="mt-3 text-[15px] font-bold tracking-[-0.02em] text-white">{preset.label}</h4>
      <p className="mt-1 line-clamp-2 text-[12px] leading-snug text-white/55">{preset.description}</p>
      <div className="mt-auto w-full pt-3">
        {hasAreas ? (
          <p className="flex items-baseline gap-1.5">
            <AnimatedNumber value={count} className="num text-[20px] font-extrabold tracking-[-0.03em] text-white" data-testid={`preset-count-${preset.id}`} />
            <span className="text-[11px] text-white/45">households</span>
          </p>
        ) : (
          <p className="text-[11px] text-white/35">Add an area to preview</p>
        )}
        <p className="mt-0.5 font-ui text-[9.5px] uppercase tracking-[0.12em] text-white/35">{preset.rule}</p>
      </div>
    </button>
  );
}

export function PresetGrid() {
  const { areas, filters, togglePreset, setFilterDrawerOpen } = useSilver();
  const counts = useMemo(
    () => Object.fromEntries(AUDIENCE_PRESETS.map((p) => [p.id, areas.length ? countForFilters(areas, presetFilters(p)) : 0])),
    [areas]
  );
  const custom = representAsPresets(filters) === null;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="preset-grid">
      {AUDIENCE_PRESETS.map((p) => (
        <PresetCard key={p.id} preset={p} active={isPresetActive(p, filters)} count={counts[p.id]} hasAreas={areas.length > 0} onClick={() => togglePreset(p)} />
      ))}
      <button type="button" onClick={() => setFilterDrawerOpen(true)} aria-pressed={custom} data-active={custom}
        className={cn("glass glass-hover trace flex min-h-[176px] flex-col items-start border-dashed p-4 text-left", custom && "glass-active")} data-testid="preset-card-advanced">
        <span className="grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/5 text-white/60">
          <SlidersHorizontal className="h-4 w-4" />
        </span>
        <h4 className="mt-3 text-[15px] font-bold tracking-[-0.02em] text-white">Advanced</h4>
        <p className="mt-1 line-clamp-2 text-[12px] leading-snug text-white/55">Build my own audience — stage, property size and opportunity signals.</p>
        <p className="mt-auto pt-3 font-ui text-[11px] font-semibold uppercase tracking-[0.12em] text-[#C6EE5A]">{custom ? "Custom audience active" : "Fine-tune audience →"}</p>
      </button>
    </div>
  );
}
